import {
  DatePicker,
  type FieldValues,
  type FormItemWrapperProps,
  NumberInput,
  PhoneInput,
  Select,
  TextArea,
  TextInput,
  type UseControllerProps,
  type UseFormReturn,
} from '@faceup/form'
import { Typography } from '@faceup/ui-base'
import { FormItemType, SHORT_STRING_MAX_LENGTH } from '@faceup/utils'
import { type AnyObject, type ArraySchema, yup } from '@faceup/validation'
import { AFFileFormInput } from '../components/AFFileFormInput'
import { AFMultiSelectFormInput } from '../components/AFMultiSelectFormInput'
import { AFScaleFormInput } from '../components/AFScaleFormInput'
import type { AFFormItem } from '../types'

export type AFFormCommonProps = UseControllerProps<FieldValues> &
  Omit<FormItemWrapperProps, 'value' | 'defaultValue' | 'placeholder'> & {
    placeholder?: string
    loading?: boolean
  }

export const useAFFormItem = () => {
  const getFormInputValueSchema = (item: AFFormItem) => {
    // A required text field is satisfied by text OR at least one voice recording, mirroring
    // the backend (SimpleText / MultilineText / MoreInformation accept voice-only answers).
    // Fields holding a recording arrive via the `$fieldsWithVoiceRecording` validation context.
    switch (item.type) {
      case FormItemType.SenderName:
        return yup.object({
          type: yup.string().oneOf([FormItemType.SenderName]).required(),
          value: yup.string().max(SHORT_STRING_MAX_LENGTH).required(),
        })
      case FormItemType.Classroom:
        return yup.object({
          type: yup.string().oneOf([FormItemType.Classroom]).required(),
          value: yup.string().max(SHORT_STRING_MAX_LENGTH).required(),
        })
      case FormItemType.SimpleText: {
        const simpleTextSchema = yup.string().max(SHORT_STRING_MAX_LENGTH)
        return yup.object({
          type: yup.string().oneOf([FormItemType.SimpleText]).required(),
          value: item.isRequired
            ? simpleTextSchema.when('$fieldsWithVoiceRecording', ([fieldIds], schema) =>
                fieldIds?.includes(item.id) ? schema : schema.required()
              )
            : simpleTextSchema,
        })
      }
      case FormItemType.MoreInformation: {
        return yup.object({
          type: yup.string().oneOf([FormItemType.MoreInformation]).required(),
          value: item.isRequired
            ? yup
                .string()
                .when('$fieldsWithVoiceRecording', ([fieldIds], schema) =>
                  fieldIds?.includes(item.id) ? schema : schema.required()
                )
            : yup.string(),
        })
      }
      case FormItemType.MultilineText: {
        let multilineTextSchema = yup.string()
        if (item.maxLength) {
          multilineTextSchema = multilineTextSchema.max(item.maxLength)
        }

        return yup.object({
          type: yup.string().oneOf([FormItemType.MultilineText]).required(),
          value: item.isRequired
            ? multilineTextSchema.when('$fieldsWithVoiceRecording', ([fieldIds], schema) =>
                fieldIds?.includes(item.id) ? schema : schema.required()
              )
            : multilineTextSchema,
        })
      }
      case FormItemType.Email: {
        const emailSchema = yup.string().email()
        return yup.object({
          type: yup.string().oneOf([FormItemType.Email]).required(),
          value: item.isRequired ? emailSchema.required() : emailSchema,
        })
      }
      case FormItemType.PhoneNumber: {
        const phoneSchema = yup.string().phone()
        return yup.object({
          type: yup.string().oneOf([FormItemType.PhoneNumber]).required(),
          value: item.isRequired ? phoneSchema.required() : phoneSchema,
        })
      }
      case FormItemType.Number: {
        let numberSchema = yup.number().positive().integer()
        if (item.isRequired) {
          numberSchema = numberSchema.required()
        }
        return yup.object({
          type: yup.string().oneOf([FormItemType.Number]).required(),
          value: numberSchema.transform(value => (value ? value : undefined)),
        })
      }
      case FormItemType.Date: {
        const dateSchema = yup.string()
        return yup.object({
          type: yup.string().oneOf([FormItemType.Date]).required(),
          value: item.isRequired ? dateSchema.required() : dateSchema,
        })
      }
      case FormItemType.Select:
        return yup.object({
          type: yup.string().oneOf([FormItemType.Select]).required(),
          value: item.isRequired
            ? item.hasOtherOption
              ? yup.string().trim().required()
              : yup
                  .string()
                  .required()
                  .oneOf(item.options.map(o => o.value))
            : item.hasOtherOption
              ? yup.string().trim()
              : yup.string().oneOf(item.options.map(o => o.value)),
        })
      case FormItemType.Category:
      case FormItemType.OrganizationalUnit:
      case FormItemType.Rating:
      case FormItemType.Scale:
      case FormItemType.YesNo:
        return yup.object({
          type: yup
            .string()
            .oneOf([
              FormItemType.Category,
              FormItemType.OrganizationalUnit,
              FormItemType.Scale,
              FormItemType.Rating,
              FormItemType.YesNo,
            ])
            .required(),
          value: item.isRequired
            ? yup
                .string()
                .required()
                .oneOf(item.options.map(o => o.value))
            : yup.string().oneOf(item.options.map(o => o.value)),
        })
      case FormItemType.MultiSelect: {
        const elementSchema = item.hasOtherOption
          ? yup.string().required() // allow free-text
          : yup
              .string()
              .oneOf(item.options.map(o => o.value))
              .required()

        const minResponses = (item.minResponses ?? 0) - (item.hasOtherOption ? 1 : 0)
        const maxResponses = item.maxResponses ?? Number.POSITIVE_INFINITY
        return yup.object({
          type: yup.string().oneOf([FormItemType.MultiSelect]).required(),
          value: item.isRequired
            ? yup.array().of(elementSchema).required().min(minResponses).max(maxResponses)
            : yup.array().of(elementSchema).min(minResponses).max(maxResponses),
        })
      }
      case FormItemType.File: {
        const fileSchema = yup.array().of(yup.mixed().file())
        return yup.object({
          type: yup.string().oneOf([FormItemType.File]).required(),
          value: item.isRequired ? fileSchema.required() : fileSchema,
        })
      }
      case FormItemType.Checkbox:
        // Only disclosure forms offer this type, and they do not render through this form. So this schema never checks a real answer.
        return yup.object({
          type: yup.string().oneOf([FormItemType.Checkbox]).required(),
          value: yup.string(),
        })
      case FormItemType.Matrix: {
        const matrixSchema = yup.object(
          item.questions.reduce<
            Record<string, ArraySchema<string[] | undefined, AnyObject, '', ''>>
          >(
            (acc, question) => ({
              ...acc,
              [question.id]: item.isRequired
                ? yup.array().of(yup.string().required()).min(1).required()
                : yup.array().of(yup.string().required()),
            }),
            {}
          )
        )
        return yup.object({
          type: yup.string().oneOf([FormItemType.Matrix]).required(),
          value: item.isRequired ? matrixSchema.required() : matrixSchema,
        })
      }
      default: {
        const exhaustiveCheck: never = item
        throw new Error(`Unhandled item type case: ${exhaustiveCheck}`)
      }
    }
  }

  const renderFormInput = (form: UseFormReturn, item: AFFormItem) => {
    const commonProps: AFFormCommonProps = {
      name: `${item.id}.value`,
      label: item.label,
      hint: item.hint ?? undefined,
      disabled: false,
      withAsterisk: item.isRequired,
      control: form.control,
    }

    switch (item.type) {
      case FormItemType.SenderName:
      case FormItemType.Classroom:
        return <TextInput {...commonProps} />
      case FormItemType.SimpleText:
        return <TextInput {...commonProps} />
      case FormItemType.Email:
        return <TextInput {...commonProps} type='email' />
      case FormItemType.MultilineText:
        return <TextArea {...commonProps} rows={4} />
      case FormItemType.MoreInformation:
        return <TextArea {...commonProps} rows={4} />
      case FormItemType.PhoneNumber:
        return <PhoneInput {...commonProps} />
      case FormItemType.Number:
        return <NumberInput {...commonProps} />
      case FormItemType.Date:
        return (
          <DatePicker
            {...commonProps}
            onChange={date => form.setValue(`${item.id}.value`, date?.toISOString() ?? '')}
          />
        )
      case FormItemType.Select:
      case FormItemType.Category:
      case FormItemType.OrganizationalUnit:
      case FormItemType.YesNo:
        return <Select {...commonProps} options={item.options} />
      case FormItemType.MultiSelect:
        return (
          <AFMultiSelectFormInput
            {...commonProps}
            maxResponses={item.maxResponses}
            options={item.options}
          />
        )
      case FormItemType.Scale:
        return <AFScaleFormInput {...commonProps} item={item} />
      case FormItemType.File:
        return <AFFileFormInput {...commonProps} />
      case FormItemType.Matrix:
      case FormItemType.Rating:
      case FormItemType.Checkbox:
        // These form item types are not supported in the preview
        return (
          <Typography.Text type='secondary'>
            Preview not available for form item type: {item.type}
          </Typography.Text>
        )
      default: {
        const exhaustiveCheck: never = item
        throw new Error(`Unhandled item type case: ${exhaustiveCheck}`)
      }
    }
  }

  return {
    getFormInputValueSchema,
    renderFormInput,
  }
}
